const STORAGE_PREFIX = 'vemmac_smart_cache_';
const VERSIONS_STORAGE_KEY = 'vemmac_cache_versions_snapshot';
const VERSIONS_STALE_MS = 5000;

let inFlightVersionsRequest = null;
let memoryVersions = { data: null, fetchedAt: 0 };

export const CACHE_VERSION_FIELDS = {
  'menu-items': 'menu_last_updated',
  combos: 'combos_last_updated',
  'dish-add-ons': 'addons_last_updated',
  'catering-menus': 'catering_last_updated',
  'rental-items': 'rentals_last_updated',
  services: 'services_last_updated',
  'payment-settings': 'payment_last_updated',
  'admin-orders': 'orders_last_updated',
  'admin-messages': null,
};

function readEntry(cacheKey) {
  if (!cacheKey || typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${cacheKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.data === undefined) return null;
    return parsed;
  } catch (error) {
    return null;
  }
}

function writeEntry(cacheKey, data, version = null) {
  if (!cacheKey || typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      `${STORAGE_PREFIX}${cacheKey}`,
      JSON.stringify({
        data,
        version,
        timestamp: Date.now(),
      })
    );
  } catch (error) {
    // Ignore quota/private mode failures.
  }
}

export function clearSmartCacheEntry(cacheKey) {
  if (!cacheKey || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(`${STORAGE_PREFIX}${cacheKey}`);
  } catch (error) {
    // Ignore storage failures.
  }
}

export async function fetchServerCacheVersions(fetchVersions) {
  const now = Date.now();
  if (memoryVersions.data && now - memoryVersions.fetchedAt < VERSIONS_STALE_MS) {
    return memoryVersions.data;
  }

  if (typeof window !== 'undefined') {
    try {
      const raw = window.sessionStorage.getItem(VERSIONS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.data && now - parsed.fetchedAt < VERSIONS_STALE_MS) {
          memoryVersions = parsed;
          return parsed.data;
        }
      }
    } catch (error) {
      // Ignore parse failures.
    }
  }

  if (!inFlightVersionsRequest) {
    inFlightVersionsRequest = fetchVersions()
      .then((data) => {
        memoryVersions = { data, fetchedAt: Date.now() };
        if (typeof window !== 'undefined') {
          try {
            window.sessionStorage.setItem(
              VERSIONS_STORAGE_KEY,
              JSON.stringify(memoryVersions)
            );
          } catch (error) {
            // Ignore storage failures.
          }
        }
        return data;
      })
      .finally(() => {
        inFlightVersionsRequest = null;
      });
  }

  return inFlightVersionsRequest;
}

export async function loadWithSmartCache({
  cacheKey,
  versionField,
  strategy = 'versioned',
  shortCacheMs = 30000,
  fetchData,
  fetchVersions,
  forceRefresh = false,
}) {
  const cached = readEntry(cacheKey);

  if (strategy === 'none' || !cacheKey || forceRefresh) {
    const data = await fetchData();
    if (strategy === 'versioned' && versionField && fetchVersions) {
      const versions = await fetchServerCacheVersions(fetchVersions);
      writeEntry(cacheKey, data, versions?.[versionField] || null);
    } else if (strategy === 'short') {
      writeEntry(cacheKey, data, null);
    }
    return { data, fromCache: false };
  }

  if (strategy === 'versioned' && versionField && fetchVersions) {
    const versions = await fetchServerCacheVersions(fetchVersions);
    const serverVersion = versions?.[versionField] || null;

    if (cached && serverVersion && cached.version === serverVersion) {
      return { data: cached.data, fromCache: true };
    }

    const data = await fetchData();
    writeEntry(cacheKey, data, serverVersion);
    return { data, fromCache: false };
  }

  if (strategy === 'short') {
    if (cached?.timestamp && Date.now() - cached.timestamp < shortCacheMs) {
      return { data: cached.data, fromCache: true };
    }

    const data = await fetchData();
    writeEntry(cacheKey, data, null);
    return { data, fromCache: false };
  }

  const data = await fetchData();
  return { data, fromCache: false };
}

export function readSmartCache(cacheKey) {
  return readEntry(cacheKey)?.data ?? null;
}
